import { Box, IconButton, InputAdornment, MenuItem, Select, Skeleton, TableCell, TableRow, TextField, Tooltip, Typography, useTheme } from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import { useTranslation } from 'react-i18next'
import NumberField from '../NumberField'
import { useDispatch, useSelector } from 'react-redux'
import { IProject, IProjectAndActivity } from '../../interface/IProjects'
import { IJournal, IJournalLine } from '../../interface/IJournal'
import { IOrdersRef } from '../../interface/IOrder'
import { IUser } from '../../interface/IUser'
import { setSelectedJournal } from '../../source/slice/JournalSlice'
import { EmptyGuid } from '../../utils/values'
import React from 'react'
import { ICalendar } from '@/interface/ICalendar'

const JournalLine = (props: any) => {
    const { row, id } = props as { row: IJournalLine; id: number }
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const theme = useTheme()

    const tableCellPadding = { padding: { xs: '16px 8px', md: '16px', lg: '16px 8px', xl: '16px' } }
    const loadingDate: boolean = useSelector((state: any) => state.loadingDate?.value)
    const projects: any = useSelector((state: any) => state.projects?.value)
    const orders: IOrdersRef[] = useSelector((state: any) => state.orders?.value)
    const selectedJournal: IJournal = useSelector((state: any) => state.journal?.value)
    const user: IUser = useSelector((state: any) => state.user?.value)
    const calendarDates: ICalendar[] = useSelector((state: any) => state.calendarDates?.value)
    const absenceBookedForSelectedJournalDay: boolean = useSelector((state: any) => state.loadingDate?.journalAbsenceBooked)

    const currentCalendarDate = calendarDates?.find((date) => {
        const journalDate = selectedJournal?.header?.date
        if (!journalDate) return false
        const today = new Date(journalDate)
        const calendarDate = new Date(date.date)
        return calendarDate.getFullYear() === today.getFullYear() && calendarDate.getMonth() === today.getMonth() && calendarDate.getDate() === today.getDate()
    })
    const isAbsenceDay = currentCalendarDate?.status === 'ABSENCE' || absenceBookedForSelectedJournalDay

    const journal: any = useSelector((state: any) => state.journal?.value)

    const handleDeleteLine = (lineno: number, fieldName: string) => {
        handleLineChange(lineno, fieldName, true)
    }

    const handleCopyLine = (line: IJournalLine) => {
        const newLine = { ...line, mserp_projjournaltransentityid: EmptyGuid }
        const newJournal = { ...selectedJournal, lines: [...(selectedJournal?.lines || []), newLine] }
        dispatch(setSelectedJournal(newJournal))
    }

    const handleDropdownChange = (lineno: number, fieldName: string) => (event: any) => {
        const value = event.target.value
        handleLineChange(lineno, fieldName, value)
    }

    const handleLineChange = (lineno: number, fieldName: string, newValue: any) => {
        const newjournallines: IJournalLine[] | undefined = journal?.lines
        if (newjournallines !== undefined) {
            const newtimecardlinesCopy = [...newjournallines]
            switch (fieldName) {
                case 'mserp_projectid':
                    const projectForAutoActivity: IProject | undefined = projects.find((p: IProject) => p.mserp_projectid === newValue)
                    const activities = projectForAutoActivity?.activities || []
                    const firstActivity: IProjectAndActivity | undefined = activities.length === 1 ? activities[0] : undefined
                    const autoActivityId = firstActivity?.mserp_activitynumber || ''

                    newtimecardlinesCopy[lineno] = {
                        ...newtimecardlinesCopy[lineno],
                        mserp_projectid: newValue,
                        mserp_activitynumber: autoActivityId,
                    }
                    break

                case 'mserp_activitynumber':
                    newtimecardlinesCopy[lineno] = {
                        ...newtimecardlinesCopy[lineno],
                        mserp_activitynumber: newValue,
                    }
                    break

                case 'mserp_purchid':
                    break

                default:
                    newtimecardlinesCopy[lineno] = {
                        ...newtimecardlinesCopy[lineno],
                        [fieldName]: newValue,
                    }
                    break
            }

            const newJournal = JSON.parse(JSON.stringify(journal))
            if (newJournal !== undefined && newJournal.lines !== undefined) {
                newJournal.lines = newtimecardlinesCopy
            }
            dispatch(setSelectedJournal(newJournal))
        }
    }

    let projectCell: React.ReactNode
    if (!loadingDate) {
        if (!selectedJournal.header?.mserp_posted && !isAbsenceDay) {
            projectCell = (
                <TextField
                    select
                    value={row.mserp_projectid ?? ''}
                    size="small"
                    fullWidth
                    onChange={handleDropdownChange(id, 'mserp_projectid')}
                    disabled={selectedJournal.header?.mserp_posted || isAbsenceDay}
                    error={selectedJournal.lines ? selectedJournal.lines[id]['mserp_projectid'] === '' : true}
                >
                    {projects?.map((project: IProject, i: number) => (
                        <MenuItem key={project.mserp_projectid + `_` + i} value={project.mserp_projectid} selected>
                            {project.mserp_projectid} - {project.mserp_projectname}
                        </MenuItem>
                    ))}
                </TextField>
            )
        } else {
            projectCell = (
                <TextField
                    value={(() => {
                        const projectId = row.mserp_projectid
                        const project = projects.find((item: IProject) => item.mserp_projectid === projectId)
                        return project ? `${project.mserp_projectid} - ${project.mserp_projectname}` : `${projectId}`
                    })()}
                    size="small"
                    fullWidth
                    disabled={true}
                />
            )
        }
    } else {
        projectCell = <Skeleton variant="text" />
    }

    let activityCell: React.ReactNode
    if (!loadingDate) {
        if (!selectedJournal.header?.mserp_posted && !isAbsenceDay) {
            activityCell = (
                <TextField
                    select
                    value={row.mserp_activitynumber ?? ''}
                    size="small"
                    fullWidth
                    onChange={handleDropdownChange(id, 'mserp_activitynumber')}
                    disabled={selectedJournal.header?.mserp_posted || isAbsenceDay}
                    error={selectedJournal.lines ? selectedJournal.lines[id]['mserp_activitynumber'] === '' : true}
                >
                    {journal?.lines ? (
                        projects
                            ?.filter((proj: IProject) => proj.mserp_projectid === row.mserp_projectid)
                            ?.map((project: IProject) =>
                                project.activities?.map((activity: IProjectAndActivity, i: number) => (
                                    <MenuItem key={activity?.mserp_projectid + `_` + i} value={activity?.mserp_activitynumber} selected>
                                        {activity?.mserp_description}
                                    </MenuItem>
                                ))
                            )
                    ) : (
                        <></>
                    )}
                </TextField>
            )
        } else {
            activityCell = (
                <TextField
                    value={(() => {
                        const projectId = row.mserp_projectid
                        const project = projects.find((item: IProject) => item.mserp_projectid === projectId)
                        const activity: IProjectAndActivity | undefined = project?.activities?.find((item: IProjectAndActivity) => item.mserp_activitynumber === row.mserp_activitynumber)
                        return activity ? `${activity.mserp_description}` : `${row.mserp_activitynumber}`
                    })()}
                    size="small"
                    fullWidth
                    disabled={true}
                />
            )
        }
    } else {
        activityCell = <Skeleton variant="text" />
    }

    const hoursValue = Math.floor(Math.round(row.mserp_hours ? row.mserp_hours * 10000 : 0) / 100) / 100
    const displayRow = !row.delete

    return displayRow ? (
        <TableRow sx={{ '&:last-child td': { border: 0 } }}>
            <TableCell sx={tableCellPadding}>
                <Tooltip title={t('zdelines.delete')}>
                    <IconButton size="small" onClick={() => handleDeleteLine(id, 'delete')} disabled={selectedJournal.header?.mserp_posted || isAbsenceDay}>
                        <DeleteOutlineIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title={t('zdelines.copy')}>
                    <IconButton size="small" onClick={() => handleCopyLine(row)} disabled={selectedJournal.header?.mserp_posted || isAbsenceDay}>
                        <ContentCopyIcon />
                    </IconButton>
                </Tooltip>
            </TableCell>
            <TableCell sx={tableCellPadding}>{projectCell}</TableCell>
            <TableCell sx={tableCellPadding}>{activityCell}</TableCell>
            {user?.worker?.mserp_employmentcategory === 'FAK' ? (
                <TableCell sx={tableCellPadding}>
                    {!loadingDate ? (
                        <Select
                            value={row.mserp_mbpurchlinerefrecid}
                            size="small"
                            fullWidth
                            onChange={handleDropdownChange(id, 'mserp_mbpurchlinerefrecid')}
                            disabled={selectedJournal.header?.mserp_posted || isAbsenceDay}
                            error={selectedJournal.lines ? selectedJournal.lines[id]['mserp_mbpurchlinerefrecid'] === 0 : true}
                            renderValue={() => {
                                const selectedOrder = orders.find((order) => order.mserp_purchlinerecid === row.mserp_mbpurchlinerefrecid)
                                if (!selectedOrder) return row.mserp_mbpurchlinerefrecid !== 0 ? `${row.mserp_mbpurchlinerefrecid}` : ''

                                return `${selectedOrder.mserp_purchid} - ${selectedOrder.mserp_linenumber}`
                            }}
                        >
                            {orders?.map((order: IOrdersRef, i: number) => {
                                const remainingQuantity = (order.mserp_remainpurchphysical ?? 0) - (order.mserp_registeredinpurchunit ?? 0)
                                return (
                                    <MenuItem key={order.mserp_purchid + `_` + i} value={order.mserp_purchlinerecid} selected>
                                        <Box>
                                            <Typography>
                                                <b>{t('zdelines.order')}:</b> {order.mserp_purchid}
                                            </Typography>
                                            <Typography>
                                                <b>{t('zdelines.position')}:</b> {order.mserp_linenumber} - &quot;{order.mserp_name}&quot;
                                            </Typography>
                                            <Typography>
                                                <b>{t('zdelines.remainingquantity')}:</b> <span style={{ color: remainingQuantity < 0 ? theme.palette.error.main : 'inherit' }}>{remainingQuantity}h</span>
                                            </Typography>
                                        </Box>
                                    </MenuItem>
                                )
                            })}
                        </Select>
                    ) : (
                        <Skeleton variant="text" />
                    )}
                </TableCell>
            ) : (
                <></>
            )}
            <TableCell sx={{ padding: { xs: '16px 16px 16px 8px', md: '16px', lg: '16px 16px 16px 8px', xl: '16px' } }}>
                {!loadingDate ? (
                    <NumberField
                        value={hoursValue}
                        size="small"
                        disabled={selectedJournal.header?.mserp_posted || isAbsenceDay}
                        sx={{ width: '120px' }}
                        onValueChange={(value: number | null) => {
                            if (value !== null && value !== undefined) {
                                handleLineChange(id, 'mserp_hours', Math.floor(Number((value * 100).toFixed(1))) / 100)
                            }
                        }}
                        format={{
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                            useGrouping: false,
                        }}
                        step={0.25}
                        min={0}
                        endAdornment={<InputAdornment position="start">h</InputAdornment>}
                        error={selectedJournal.lines?.[id]?.mserp_hours !== undefined ? selectedJournal.lines[id].mserp_hours < 0 || selectedJournal.lines[id].mserp_hours % 0.25 !== 0 : true}
                        helperText={selectedJournal.lines?.[id]?.mserp_hours !== undefined && selectedJournal.lines[id].mserp_hours % 0.25 !== 0 ? t('zdelines.journalinvalidhours') : ''}
                    />
                ) : (
                    <Skeleton variant="text" />
                )}
            </TableCell>
        </TableRow>
    ) : (
        <></>
    )
}

export default JournalLine
